import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React, { useMemo, useRef, useState } from 'react';
import { FlatList, Linking, RefreshControl, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/ThemedText';
import AppStyles from '@/constants/Styles';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useNavigationStore } from '@/stores/navigationStore';
import { Address, AddressListProps, GroupedAddress, Voter } from '@/types/canvassing';

interface ExtendedAddressListProps extends AddressListProps {
  onScroll?: (event: any) => void;
  searchQuery?: string;
  onShowAddressOnMap?: (postcode: string) => void;
  expandedStreets: Set<string>;
  onToggleStreet: (streetKey: string) => void;
  onRefresh?: () => Promise<void>;
  refreshing?: boolean;
  isPhoneEngagement?: boolean;
  getAttemptNumber?: (address: Address) => number; // Function to get attempt number (uses stored assignments if available)
  groupByStreet?: boolean;
  onGroupByStreetChange?: (groupByStreet: boolean) => void;
}

interface AttemptGroup {
  attemptNumber: number;
  attemptLabel: string;
  streetGroups: StreetGroup[];
}

interface StreetGroup {
  streetKey: string;
  streetName: string;
  streetSubName: string;
  addresses: GroupedAddress[];
  totalAddresses: number;
  visitedCount: number;
  visitedOnlyCount: number;
  notInCount: number;
}

export default function AddressList({ addresses, engagementId, battlePlanId, searchQuery, onScroll, onShowAddressOnMap, expandedStreets, onToggleStreet, onRefresh, refreshing, isPhoneEngagement = false, battlePlanType, getAttemptNumber: getAttemptNumberProp, groupByStreet: groupByStreetProp, onGroupByStreetChange }: ExtendedAddressListProps) {
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { getSelectedBranch, getSelectedCampaign } = useNavigationStore();
  const selectedBranch = getSelectedBranch();
  const selectedCampaign = getSelectedCampaign();
  
  // Theme-aware colors using useThemeColor
  const iconColor = useThemeColor({}, 'icon');
  const cardBackground = useThemeColor({}, 'cardBackground' as any);
  const backgroundColor = useThemeColor({}, 'background');
  const borderColor = useThemeColor({}, 'border' as any);
  const textColor = useThemeColor({}, 'text');
  const secondaryTextColor = useThemeColor({}, 'secondaryText' as any);
  const errorColor = useThemeColor({}, 'errorText' as any);
  const successColor = useThemeColor({}, 'successText' as any);
  const progressBackground = useThemeColor({}, 'progressBackground' as any);
  const tintColor = useThemeColor({}, 'tint');
  // Progress bar colors using Colors.ts values
  const progressVisitedColor = useThemeColor({}, 'progressVisited' as any);
  const progressNotInColor = useThemeColor({}, 'progressNotIn' as any);
  // Status background colors using useThemeColor
  const visitedBackgroundColor = useThemeColor({}, 'visitedBackground' as any);
  const notInBackgroundColor = useThemeColor({}, 'notInBackground' as any);
  // Street header background - transparent in dark mode
  const streetHeaderBackground = useThemeColor({ light: '#ffffff', dark: 'transparent' }, 'background');
  
  // Track last search query to prevent unnecessary effects
  const lastSearchQuery = useRef<string>('');
  
  // Track expanded attempt groups (will be initialized with lowest attempt number)
  const [expandedAttempts, setExpandedAttempts] = React.useState<Set<number>>(new Set());
  const lastEngagementId = useRef<string>(engagementId);
  
  // Reset expanded attempts when engagement changes
  React.useEffect(() => {
    if (lastEngagementId.current !== engagementId) {
      lastEngagementId.current = engagementId;
      setExpandedAttempts(new Set()); // Reset expanded attempts when engagement changes
    }
  }, [engagementId]);
  
  // Grouping state - use prop if provided, otherwise default to false for phone campaigns, true for others
  const [internalGroupByStreet] = useState<boolean>(!isPhoneEngagement);
  const groupByStreet = groupByStreetProp !== undefined ? groupByStreetProp : internalGroupByStreet;

  // Determine if this is a GOTV plan
  const isGotvPlan = battlePlanType === 'gotv-door-knocking' || battlePlanType === 'gotv-phoning';

  // Get attempt number for an address
  // Uses prop function if provided (from parent), otherwise calculates from address data
  const getAttemptNumber = (address: Address): number => {
    if (getAttemptNumberProp) {
      return getAttemptNumberProp(address);
    }
    
    // Fallback to calculating from address data
    if (isGotvPlan) {
      return address.total_interactions_this_engagement ?? 0;
    } else {
      return address.total_attempts_last_3_months ?? 0;
    }
  };

  // Convert attempt number to attempt label (0 = "1st attempt", 1 = "2nd attempt", etc.)
  const getAttemptLabel = (attemptNumber: number): string => {
    const attemptIndex = attemptNumber + 1;
    // Handle special cases for 11th, 12th, 13th
    if (attemptIndex >= 11 && attemptIndex <= 13) {
      return `${attemptIndex}th attempt`;
    }
    // Handle 1st, 2nd, 3rd
    const lastDigit = attemptIndex % 10;
    if (lastDigit === 1) return `${attemptIndex}st attempt`;
    if (lastDigit === 2) return `${attemptIndex}nd attempt`;
    if (lastDigit === 3) return `${attemptIndex}rd attempt`;
    // Everything else is "th"
    return `${attemptIndex}th attempt`;
  };

  // Helper function to check if an address is "previously visited" 
  // (visited and either has timestamp > 2 hours ago, or is marked as visited without a timestamp)
  const isPreviouslyVisited = (address: Address): boolean => {
    if (!address.visited) {
      return false;
    }

    // If no timestamp, consider it previously visited (e.g., GOTV-visited addresses)
    if (!address.latest_interaction_timestamp) {
      return true;
    }

    try {
      const now = new Date();
      const twoHoursAgo = new Date(now.getTime() - (2 * 60 * 60 * 1000)); // 2 hours ago
      const timestamp = new Date(address.latest_interaction_timestamp);
      
      // If timestamp is more than 2 hours ago, it's previously visited
      return timestamp < twoHoursAgo;
    } catch (error) {
      // If timestamp parsing fails, consider it previously visited to be safe
      return true;
    }
  };

  // Convert addresses to grouped format and sort voters by electoral number
  const groupedAddresses = useMemo(() => {
    return addresses.map(address => {
      const grouped: GroupedAddress = {
        address_id: address.address_id,
        full_address_display: [
          address.house_name,
          address.house_number,
          address.street_sub_name,
          address.street_name,
        ].filter(Boolean).join(', '),
        voters: address.voters,
        not_in: address.not_in,
        visited: address.visited,
        voters_in_scope: address.voters_in_scope,
        total_voters_at_address: address.total_voters_at_address,
        attemptNumber: getAttemptNumber(address)
      };
      // Include individual address components for sorting (only if they exist)
      if (address.house_name !== undefined) grouped.house_name = address.house_name;
      if (address.house_number !== undefined) grouped.house_number = address.house_number;
      if (address.street_name !== undefined) grouped.street_name = address.street_name;
      if (address.street_sub_name !== undefined) grouped.street_sub_name = address.street_sub_name;
      return grouped;
    });
  }, [addresses, isGotvPlan]);

  // Helper function to sort addresses
  const sortAddresses = (addresses: GroupedAddress[]): GroupedAddress[] => {
    return addresses.sort((a, b) => {
      // Compare street names first
      const aStreet = a.street_name || '';
      const bStreet = b.street_name || '';
      
      if (aStreet !== bStreet) {
        return aStreet.localeCompare(bStreet);
      }
      
      // If same street, compare sub street name
      const aSubStreet = a.street_sub_name || '';
      const bSubStreet = b.street_sub_name || '';
      
      if (aSubStreet !== bSubStreet) {
        return aSubStreet.localeCompare(bSubStreet);
      }
      
      // If same street and sub street, compare house names first (alphabetically)
      const aHouseName = a.house_name || '';
      const bHouseName = b.house_name || '';
      
      if (aHouseName !== bHouseName) {
        // If one has a house name and the other doesn't, house name comes first
        if (aHouseName && !bHouseName) return -1;
        if (!aHouseName && bHouseName) return 1;
        // Both have house names, sort alphabetically
        return aHouseName.localeCompare(bHouseName);
      }
      
      // If house names are the same (or both empty), compare house numbers numerically
      const aHouseNumber = a.house_number || '';
      const bHouseNumber = b.house_number || '';
      
      // Function to parse house number for sorting
      const parseHouseNumber = (houseNumber: string) => {
        // Extract numeric part and letter part
        const match = houseNumber.match(/^(\d+)([a-zA-Z]*)$/);
        if (!match) {
          // If no match, try to extract just the number
          const num = parseInt(houseNumber.replace(/\D/g, ''), 10) || 0;
          return { number: num, letter: '', original: houseNumber };
        }
        const [, numStr, letter] = match;
        const number = parseInt(numStr || '0', 10);
        return { number, letter: (letter || '').toLowerCase(), original: houseNumber };
      };
      
      const aParsed = parseHouseNumber(aHouseNumber);
      const bParsed = parseHouseNumber(bHouseNumber);
      
      // Compare numeric part first
      if (aParsed.number !== bParsed.number) {
        return aParsed.number - bParsed.number;
      }
      
      // If numbers are the same, compare letters (empty letter comes before any letter)
      if (aParsed.letter !== bParsed.letter) {
        if (aParsed.letter === '') return -1; // No letter comes first
        if (bParsed.letter === '') return 1;  // No letter comes first
        return aParsed.letter.localeCompare(bParsed.letter);
      }
      
      // If both number and letter are the same, compare the original string
      return aParsed.original.localeCompare(bParsed.original);
    });
  };

  // Separate addresses into regular and previously visited
  const { regularAddresses, previouslyVisitedAddresses } = useMemo(() => {
    const regular: GroupedAddress[] = [];
    const previouslyVisited: GroupedAddress[] = [];
    
    groupedAddresses.forEach(address => {
      // Check if address is previously visited using the original address data
      const originalAddress = addresses.find(a => a.address_id === address.address_id);
      if (originalAddress && isPreviouslyVisited(originalAddress)) {
        previouslyVisited.push(address);
      } else {
        regular.push(address);
      }
    });
    
    return { regularAddresses: regular, previouslyVisitedAddresses: previouslyVisited };
  }, [groupedAddresses, addresses]);

  // Group addresses by attempt number first, then by street
  const attemptGroups = useMemo(() => {
    // First, group by attempt number (only for regular addresses)
    const attemptGroupsMap: { [key: number]: GroupedAddress[] } = {};
    
    regularAddresses.forEach(address => {
      const attemptNum = (address as any).attemptNumber || 0;
      if (!attemptGroupsMap[attemptNum]) {
        attemptGroupsMap[attemptNum] = [];
      }
      attemptGroupsMap[attemptNum].push(address);
    });

    // For each attempt group, sort addresses and optionally group by street
    const attemptGroupsArray: AttemptGroup[] = Object.entries(attemptGroupsMap)
      .map(([attemptNumStr, addresses]) => {
        const attemptNumber = parseInt(attemptNumStr, 10);
        const sortedAddresses = sortAddresses(addresses);
        
        let streetGroups: StreetGroup[] = [];
        
        if (groupByStreet) {
          // Group by street within this attempt group
          const streetGroupsMap: { [key: string]: GroupedAddress[] } = {};
          
          sortedAddresses.forEach(address => {
            const streetKey = `${address.street_name || 'Unknown Street'}_${address.street_sub_name || ''}`;
            if (!streetGroupsMap[streetKey]) {
              streetGroupsMap[streetKey] = [];
            }
            streetGroupsMap[streetKey].push(address);
          });

          // Convert street groups to array format
          streetGroups = Object.entries(streetGroupsMap).map(([streetKey, streetAddresses]) => {
            const [streetName, streetSubName] = streetKey.split('_');
            const visitedOnly = streetAddresses.filter(addr => addr.visited && !addr.not_in).length;
            const notInOnly = streetAddresses.filter(addr => addr.not_in).length;
            const totalVisited = visitedOnly + notInOnly;
            
            return {
              streetKey: `${attemptNumber}_${streetKey}`, // Prefix with attempt number to make unique
              streetName: streetName || 'Unknown Street',
              streetSubName: streetSubName || '',
              addresses: streetAddresses,
              totalAddresses: streetAddresses.length,
              visitedCount: totalVisited,
              visitedOnlyCount: visitedOnly,
              notInCount: notInOnly,
            };
          });
        } else {
          // If not grouping by street, create a single "group" with all addresses
          const visitedOnly = sortedAddresses.filter(addr => addr.visited && !addr.not_in).length;
          const notInOnly = sortedAddresses.filter(addr => addr.not_in).length;
          const totalVisited = visitedOnly + notInOnly;
          
          streetGroups = [{
            streetKey: `${attemptNumber}_all`,
            streetName: '',
            streetSubName: '',
            addresses: sortedAddresses,
            totalAddresses: sortedAddresses.length,
            visitedCount: totalVisited,
            visitedOnlyCount: visitedOnly,
            notInCount: notInOnly,
          }];
        }

        return {
          attemptNumber,
          attemptLabel: getAttemptLabel(attemptNumber),
          streetGroups,
        };
      })
      .sort((a, b) => a.attemptNumber - b.attemptNumber); // Sort by attempt number ascending

    return attemptGroupsArray;
  }, [regularAddresses, groupByStreet]);

  // Create "Previously Visited" attempt group
  const previouslyVisitedGroup = useMemo(() => {
    if (previouslyVisitedAddresses.length === 0) {
      return null;
    }

    const sortedAddresses = sortAddresses(previouslyVisitedAddresses);
    let streetGroups: StreetGroup[] = [];
    
    if (groupByStreet) {
      // Group by street within previously visited addresses
      const streetGroupsMap: { [key: string]: GroupedAddress[] } = {};
      
      sortedAddresses.forEach(address => {
        const streetKey = `${address.street_name || 'Unknown Street'}_${address.street_sub_name || ''}`;
        if (!streetGroupsMap[streetKey]) {
          streetGroupsMap[streetKey] = [];
        }
        streetGroupsMap[streetKey].push(address);
      });

      // Convert street groups to array format
      streetGroups = Object.entries(streetGroupsMap).map(([streetKey, streetAddresses]) => {
        const [streetName, streetSubName] = streetKey.split('_');
        const visitedOnly = streetAddresses.filter(addr => addr.visited && !addr.not_in).length;
        const notInOnly = streetAddresses.filter(addr => addr.not_in).length;
        const totalVisited = visitedOnly + notInOnly;
        
        return {
          streetKey: `previously-visited_${streetKey}`, // Prefix to make unique
          streetName: streetName || 'Unknown Street',
          streetSubName: streetSubName || '',
          addresses: streetAddresses,
          totalAddresses: streetAddresses.length,
          visitedCount: totalVisited,
          visitedOnlyCount: visitedOnly,
          notInCount: notInOnly,
        };
      });
    } else {
      // If not grouping by street, create a single "group" with all addresses
      const visitedOnly = sortedAddresses.filter(addr => addr.visited && !addr.not_in).length;
      const notInOnly = sortedAddresses.filter(addr => addr.not_in).length;
      const totalVisited = visitedOnly + notInOnly;
      
      streetGroups = [{
        streetKey: 'previously-visited_all',
        streetName: '',
        streetSubName: '',
        addresses: sortedAddresses,
        totalAddresses: sortedAddresses.length,
        visitedCount: totalVisited,
        visitedOnlyCount: visitedOnly,
        notInCount: notInOnly,
      }];
    }

    return {
      attemptNumber: -1, // Use -1 to ensure it sorts to the bottom
      attemptLabel: 'Previously Visited',
      streetGroups,
    };
  }, [previouslyVisitedAddresses, groupByStreet]);

  // Toggle attempt group expansion
  const toggleAttemptExpansion = (attemptNumber: number) => {
    setExpandedAttempts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(attemptNumber)) {
        newSet.delete(attemptNumber);
      } else {
        newSet.add(attemptNumber);
      }
      return newSet;
    });
  };

  // Initialize expanded attempts with the lowest attempt number when attemptGroups are first loaded
  React.useEffect(() => {
    if (attemptGroups.length > 0 && expandedAttempts.size === 0) {
      // Find the lowest attempt number (attemptGroups are already sorted ascending)
      const firstAttempt = attemptGroups[0];
      if (firstAttempt !== undefined) {
        setExpandedAttempts(new Set([firstAttempt.attemptNumber]));
      }
    }
  }, [attemptGroups]);

  // Combine auto-expand and collapse logic into a single effect
  React.useEffect(() => {
    // Only run when search query actually changes
    if (lastSearchQuery.current === searchQuery) return;
    lastSearchQuery.current = searchQuery || '';
    
    if (!searchQuery || searchQuery.trim() === '') {
      // Clear all expanded streets through parent
      expandedStreets.forEach(streetKey => onToggleStreet(streetKey));
    } else if (attemptGroups.length > 0) {
      // Expand first attempt group and first street if searching
      const firstAttempt = attemptGroups[0];
      if (firstAttempt?.streetGroups && firstAttempt.streetGroups.length > 0) {
        setExpandedAttempts(new Set([firstAttempt.attemptNumber]));
        const targetStreet = firstAttempt.streetGroups[0]?.streetKey;
        if (targetStreet) {
          expandedStreets.forEach(streetKey => {
            if (streetKey !== targetStreet) onToggleStreet(streetKey);
          });
          if (!expandedStreets.has(targetStreet)) {
            onToggleStreet(targetStreet);
          }
        }
      }
    }
  }, [searchQuery, attemptGroups, onToggleStreet, expandedStreets]);

  const getStatusIcon = (address: GroupedAddress) => {
    if (address.not_in) {
      // Slightly red background for "not in" addresses using Colors.ts
      return { icon: 'close', color: errorColor, bgColor: notInBackgroundColor };
    } else if (address.visited) {
      // Slightly green background for visited addresses using Colors.ts
      return { icon: 'check', color: successColor, bgColor: visitedBackgroundColor };
    } else {
      return { icon: 'remove', color: secondaryTextColor, bgColor: cardBackground };
    }
  };

  const getStatusText = (address: GroupedAddress) => {
    if (address.not_in) return 'Not In';
    if (address.visited) return 'Visited';
    
    // Show attempt count based on attemptNumber (0 = "Not Visited", 1 = "Attempted Once", 2 = "Attempted Twice", etc.)
    const attemptNumber = address.attemptNumber || 0;
    
    // If attemptNumber is 0, show "Not Visited"
    if (attemptNumber === 0) {
      return 'Not Visited';
    }
    
    // Handle 1st, 2nd, 3rd
    const lastDigit = attemptNumber % 10;
    if (lastDigit === 1) return 'Attempted Once';
    if (lastDigit === 2) return 'Attempted Twice';
    if (lastDigit === 3) return 'Attempted Thrice';
    
    // Everything else is "X Times"
    return `Attempted ${attemptNumber} Times`;
  };

  const toggleStreetExpansion = (streetKey: string) => {
    // Always delegate to parent
    onToggleStreet(streetKey);
  };

  // Helper function to get GOTV status icon and letter
  const getGotvStatus = (voter: Voter) => {
    if (voter.voted_yet) return { icon: 'check-circle', letter: 'V' };
    if (voter.will_not_vote) return { icon: 'cancel', letter: 'W' };
    if (voter.requested_lift) return { icon: 'directions-car', letter: 'L' };
    // Backward compatibility: has_been_contacted means will vote (todo)
    if (voter.has_been_contacted) {
      return { icon: 'how-to-vote', letter: 'T' };
    }
    return null;
  };

  // Helper function to format phone number: add 0 if it doesn't start with 0 or +
  const formatPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber || phoneNumber.trim() === '') {
      return phoneNumber;
    }
    const trimmed = phoneNumber.trim();
    // If it doesn't start with 0 or +, add 0 at the beginning
    if (!trimmed.startsWith('0') && !trimmed.startsWith('+')) {
      return `0${trimmed}`;
    }
    return trimmed;
  };

  const handlePhoneCall = async (phoneNumber: string) => {
    try {
      const formattedNumber = formatPhoneNumber(phoneNumber);
      const phoneUrl = `tel:${formattedNumber}`;
      const canOpen = await Linking.canOpenURL(phoneUrl);
      if (canOpen) {
        await Linking.openURL(phoneUrl);
      } else {
        console.warn('Cannot open phone URL:', phoneUrl);
      }
    } catch (error) {
      console.error('Error opening phone URL:', error);
    }
  };

  const renderVoter = ({ item: voter }: { item: Voter }) => {
    const gotvStatus = getGotvStatus(voter);
    const hasPhoneNumber = voter.phone_number && voter.phone_number.trim() !== '';
    const formattedPhoneNumber = hasPhoneNumber ? formatPhoneNumber(voter.phone_number!) : '';
    
    return (
      <View style={[AppStyles.voterRow, { borderBottomColor: borderColor }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
            <ThemedText style={AppStyles.voterName}>{voter.name}</ThemedText>
            {voter.av_description === 'Postal' && (
                <MaterialIcons name="email" size={18} color={iconColor} style={{ marginLeft: 8 }} />
            )}
            {hasPhoneNumber && (
              <TouchableOpacity
                onPress={() => handlePhoneCall(voter.phone_number!)}
                style={{ marginLeft: 8, flexDirection: 'row', alignItems: 'center' }}
                accessibilityLabel={`Call ${formattedPhoneNumber}`}
                accessibilityHint="Opens phone dialer to call this voter"
              >
                <MaterialIcons name="phone" size={16} color={tintColor} style={{ marginRight: 4 }} />
                <ThemedText style={{ fontSize: 14, color: tintColor }}>{formattedPhoneNumber}</ThemedText>
              </TouchableOpacity>
            )}
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            {gotvStatus && (
              <MaterialIcons name={gotvStatus.icon as any} size={18} color={iconColor} />
            )}
            <ThemedText style={AppStyles.voterVi}>{voter.latest_vi}</ThemedText>
          </View>
        </View>
      </View>
    );
  };

  const handleHouseholdPress = (address: GroupedAddress) => {
    if (selectedBranch?.branch_short_name && selectedCampaign?.boundary_id) {
      const routeType = isPhoneEngagement ? 'phone' : 'canvass';
      router.push({
        pathname: `/(main)/[branch]/campaign/[campaignId]/${routeType}/[battlePlanId]/[engagementId]/[addressId]`,
        params: {
          branch: selectedBranch.branch_short_name,
          campaignId: selectedCampaign.boundary_id,
          battlePlanId: battlePlanId,
          engagementId: engagementId,
          addressId: address.address_id,
        },
      });
    }
  };

  const handleLocationPress = (address: GroupedAddress, event: any) => {
    // Prevent the household press event from firing
    event.stopPropagation();
    
    const addressData = addresses.find(addr => addr.address_id === address.address_id);
    const postcode = addressData?.postcode;
    
    if (postcode && onShowAddressOnMap) {
      // Call the parent callback to show the address on map
      onShowAddressOnMap(postcode);
    }
  };

  const renderAddress = ({ item: address }: { item: GroupedAddress }) => {
    const statusInfo = getStatusIcon(address);
    const statusText = getStatusText(address);
    const addressData = addresses.find(addr => addr.address_id === address.address_id);
    const hasPostcode = addressData?.postcode && addressData.postcode.trim() !== '';
    
    return (
      <TouchableOpacity onPress={() => handleHouseholdPress(address)}>
        <View style={[AppStyles.addressCard, { backgroundColor: statusInfo.bgColor, position: 'relative' }]}>
          <View style={AppStyles.addressHeaderRow}>
            {hasPostcode && (
              <TouchableOpacity
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: backgroundColor,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderWidth: 0.5,
                  borderColor: borderColor,
                  marginRight: 8,
                  marginTop: -2,
                  flexShrink: 0,
                }}
                onPress={(event) => handleLocationPress(address, event)}
                accessibilityLabel={`Show ${addressData?.postcode} on map`}
                accessibilityHint="Shows the map with this address's postcode highlighted"
              >
                <MaterialIcons name="location-on" size={18} color={tintColor} />
              </TouchableOpacity>
            )}
            <ThemedText style={[AppStyles.addressHeader, { borderBottomColor: borderColor }]}>{address.full_address_display}</ThemedText>
            <View style={AppStyles.statusIndicator}>
              <View style={[AppStyles.statusIcon, { backgroundColor: statusInfo.color }]}>
                <MaterialIcons name={statusInfo.icon as any} size={16} color="white" />
              </View>
              <ThemedText style={[AppStyles.statusText, { color: statusInfo.color }]}>{statusText}</ThemedText>
            </View>
          </View>
          <FlatList
            data={address.voters}
            renderItem={renderVoter}
            keyExtractor={(voter) => voter.voter_id}
            scrollEnabled={false}
          />
        </View>
      </TouchableOpacity>
    );
  };

  const renderAttemptHeader = (attemptGroup: AttemptGroup) => {
    const isExpanded = expandedAttempts.has(attemptGroup.attemptNumber);
    
    return (
      <TouchableOpacity 
        onPress={() => toggleAttemptExpansion(attemptGroup.attemptNumber)}
        style={{ 
          paddingHorizontal: 16, 
          paddingVertical: 12, 
          backgroundColor: cardBackground 
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <MaterialIcons 
            name={isExpanded ? 'expand-less' : 'expand-more'} 
            size={20} 
            color={iconColor}
            style={{ marginRight: 8 }}
          />
          <ThemedText style={AppStyles.headerText}>
            {attemptGroup.attemptLabel}
          </ThemedText>
        </View>
      </TouchableOpacity>
    );
  };

  const renderStreetHeader = (streetGroup: StreetGroup) => {
    const isExpanded = expandedStreets.has(streetGroup.streetKey);
    const streetDisplayName = streetGroup.streetSubName 
      ? `${streetGroup.streetSubName}, ${streetGroup.streetName}`
      : streetGroup.streetName;
    
    // Check if street is completed
    const isCompleted = streetGroup.visitedCount === streetGroup.totalAddresses;
    
    return (
      <TouchableOpacity 
        onPress={() => toggleStreetExpansion(streetGroup.streetKey)}
        style={[
          AppStyles.streetHeader,
          { 
            backgroundColor: streetHeaderBackground,
            borderBottomColor: borderColor 
          }
        ]}
      >
        <View style={AppStyles.streetHeaderContent}>
          <View style={AppStyles.streetHeaderLeft}>
            <MaterialIcons 
              name={isExpanded ? 'expand-less' : 'expand-more'} 
              size={24} 
              color={isCompleted ? secondaryTextColor : iconColor} 
            />
            <ThemedText style={[
              AppStyles.streetHeaderTitle,
              { color: isCompleted ? secondaryTextColor : textColor },
              isCompleted && AppStyles.streetHeaderTitleCompleted
            ]}>
              {streetDisplayName}
            </ThemedText>
          </View>
          <View style={AppStyles.streetHeaderStats}>
            <ThemedText style={[
              AppStyles.streetHeaderCount,
              { color: isCompleted ? secondaryTextColor : undefined },
              isCompleted && AppStyles.streetHeaderCountCompleted
            ]}>
              {streetGroup.visitedCount}/{streetGroup.totalAddresses} visited
            </ThemedText>
          </View>
        </View>
        {/* Progress Bar */}
        <View style={AppStyles.streetProgressContainer}>
          <View style={[AppStyles.streetProgressBackground, { backgroundColor: progressBackground }]}>
            {/* Visited portion */}
            {streetGroup.visitedOnlyCount > 0 && (
              <View 
                style={[
                  AppStyles.streetProgressVisited,
                  { 
                    backgroundColor: progressVisitedColor,
                    width: `${(streetGroup.visitedOnlyCount / streetGroup.totalAddresses) * 100}%` 
                  }
                ]} 
              />
            )}
            {/* Not in portion */}
            {streetGroup.notInCount > 0 && (
              <View 
                style={[
                  AppStyles.streetProgressNotIn,
                  { 
                    backgroundColor: progressNotInColor,
                    width: `${(streetGroup.notInCount / streetGroup.totalAddresses) * 100}%`,
                    left: `${(streetGroup.visitedOnlyCount / streetGroup.totalAddresses) * 100}%`
                  }
                ]} 
              />
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  // Flatten attempt groups and street groups for FlatList
  const listData = useMemo(() => {
    const items: Array<{ type: 'attempt' | 'street' | 'address', attemptGroup?: AttemptGroup, streetGroup?: StreetGroup, address?: GroupedAddress }> = [];
    
    // Add regular attempt groups
    attemptGroups.forEach(attemptGroup => {
      const isAttemptExpanded = expandedAttempts.has(attemptGroup.attemptNumber);
      
      // Add attempt header
      items.push({ type: 'attempt', attemptGroup });
      
      // Add street groups if attempt is expanded
      if (isAttemptExpanded) {
        attemptGroup.streetGroups.forEach(streetGroup => {
          const isStreetExpanded = expandedStreets.has(streetGroup.streetKey);
          
          // Add street header only if grouping by street
          if (groupByStreet) {
            items.push({ type: 'street', streetGroup });
          }
          
          // Add addresses if street is expanded (or always if not grouping by street)
          if (!groupByStreet || isStreetExpanded) {
            streetGroup.addresses.forEach(address => {
              items.push({ type: 'address', address });
            });
          }
        });
      }
    });
    
    // Add "Previously Visited" group at the bottom if it exists
    if (previouslyVisitedGroup) {
      const isPreviouslyVisitedExpanded = expandedAttempts.has(-1);
      
      // Add attempt header
      items.push({ type: 'attempt', attemptGroup: previouslyVisitedGroup });
      
      // Add street groups if expanded
      if (isPreviouslyVisitedExpanded) {
        previouslyVisitedGroup.streetGroups.forEach(streetGroup => {
          const isStreetExpanded = expandedStreets.has(streetGroup.streetKey);
          
          // Add street header only if grouping by street
          if (groupByStreet) {
            items.push({ type: 'street', streetGroup });
          }
          
          // Add addresses if street is expanded (or always if not grouping by street)
          if (!groupByStreet || isStreetExpanded) {
            streetGroup.addresses.forEach(address => {
              items.push({ type: 'address', address });
            });
          }
        });
      }
    }
    
    return items;
  }, [attemptGroups, previouslyVisitedGroup, expandedAttempts, expandedStreets, groupByStreet]);

  const renderListItem = ({ item }: { item: { type: 'attempt' | 'street' | 'address', attemptGroup?: AttemptGroup, streetGroup?: StreetGroup, address?: GroupedAddress } }) => {
    if (item.type === 'attempt' && item.attemptGroup) {
      return (
        <View>
          {renderAttemptHeader(item.attemptGroup)}
        </View>
      );
    } else if (item.type === 'street' && item.streetGroup) {
      return (
        <View>
          {renderStreetHeader(item.streetGroup)}
        </View>
      );
    } else if (item.type === 'address' && item.address) {
      return (
        <View style={AppStyles.streetAddressesContainer}>
          {renderAddress({ item: item.address })}
        </View>
      );
    }
    return null;
  };


  return (
    <>
      {/* Attempt Groups with Street Groups */}
      <FlatList
        data={listData}
        keyExtractor={(item, index) => {
          if (item.type === 'attempt' && item.attemptGroup) {
            return `attempt-${item.attemptGroup.attemptNumber}`;
          } else if (item.type === 'street' && item.streetGroup) {
            return `street-${item.streetGroup.streetKey}`;
          } else if (item.type === 'address' && item.address) {
            return `address-${item.address.address_id}`;
          }
          return `item-${index}`;
        }}
        renderItem={renderListItem}
        contentContainerStyle={{ paddingTop: 12, paddingBottom: 12 + insets.bottom }}
        onScroll={onScroll}
        scrollEventThrottle={16}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing || false}
              onRefresh={onRefresh}
              tintColor={tintColor}
              colors={[tintColor]}
            />
          ) : undefined
        }
        ListEmptyComponent={
          <View style={AppStyles.emptyContainer}>
            <ThemedText>No voters found.</ThemedText>
          </View>
        }
      />
    </>
  );
}
